import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IAuditDetailResponse } from '../../../model/base/response/audit-response';

interface AuditDetailState {
  audit: IAuditDetailResponse | null;
  loading: boolean;
  error: string | null;
}

type AuditDetailAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: IAuditDetailResponse }
  | { type: 'LOAD_ERROR'; payload: string };

const initialState: AuditDetailState = {
  audit: null,
  loading: false,
  error: null,
};

const AuditDetailStateContext = createContext<AuditDetailState | undefined>(undefined);
const AuditDetailDispatchContext = createContext<React.Dispatch<AuditDetailAction> | undefined>(undefined);

function auditDetailReducer(state: AuditDetailState, action: AuditDetailAction): AuditDetailState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: null };
    case 'LOAD_SUCCESS':
      return { ...state, loading: false, audit: action.payload };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

export function AuditDetailProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(auditDetailReducer, initialState);

  return (
    <AuditDetailStateContext.Provider value={state}>
      <AuditDetailDispatchContext.Provider value={dispatch}>
        {children}
      </AuditDetailDispatchContext.Provider>
    </AuditDetailStateContext.Provider>
  );
}

export function useAuditDetailState() {
  const context = useContext(AuditDetailStateContext);
  if (context === undefined) {
    throw new Error('useAuditDetailState must be used within AuditDetailProvider');
  }
  return context;
}

export function useAuditDetailDispatch() {
  const context = useContext(AuditDetailDispatchContext);
  if (context === undefined) {
    throw new Error('useAuditDetailDispatch must be used within AuditDetailProvider');
  }
  return context;
}
